import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { User, Gauge, ClipboardList, CheckCircle2, MessageCircle, ArrowRight, X, Sparkles } from "lucide-react";

const buildSteps = (weeks) => [
  {
    Icon: Sparkles,
    title: "Bienvenue sur MAIRUNCOACH",
    body: "Ton coaching de course à pied, piloté par l'IA et supervisé par un coach humain. Voici les étapes clés en 30 secondes.",
  },
  {
    Icon: User,
    title: "1. Complète ton profil",
    body: "Ton objectif, ton niveau et tes disponibilités affinent ton plan. Tu peux les ajuster à tout moment depuis la page Profil.",
  },
  {
    Icon: Gauge,
    title: "2. Ta VMA (ou démarre sans)",
    body: "La VMA calibre tes allures précises. Si tu la connais, renseigne-la dans ton Profil ; sinon, lance un test demi-Cooper ou démarre sans — tu régénéreras tes allures plus tard.",
  },
  {
    Icon: ClipboardList,
    title: "3. Découvre ton programme",
    body: `Ton cycle de ${weeks} semaines est généré et progressif. Retrouve toutes tes séances sur la page Programme, semaine par semaine.`,
  },
  {
    Icon: CheckCircle2,
    title: "4. Valide tes séances",
    body: "Lance la séance du jour avec COMMENCER, puis donne ton ressenti à la fin. Tes retours adaptent la suite de ton entraînement.",
  },
  {
    Icon: MessageCircle,
    title: "5. Ton assistant IA",
    body: "Une question, un ajustement, un doute ? L'assistant IA est disponible en continu depuis la page Assistant. Bonne course !",
  },
];

export default function WelcomeTutorial() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [closing, setClosing] = useState(false);
  const [weeks, setWeeks] = useState(4);

  useEffect(() => {
    api.get("/plans").then((r) => { if (r.data.weeks_per_cycle) setWeeks(r.data.weeks_per_cycle); }).catch(() => {});
  }, []);

  const STEPS = buildSteps(weeks);
  const shouldShow = user && user.role === "client" && !user.tutorial_completed;
  if (!shouldShow || closing) return null;

  const finish = async () => {
    setClosing(true);
    setUser({ ...user, tutorial_completed: true });
    try { await api.post("/onboarding/tutorial-complete"); } catch { /* non bloquant */ }
  };

  const isLast = step === STEPS.length - 1;
  const s = STEPS[step];
  const Icon = s.Icon;

  return (
    <div data-testid="welcome-tutorial" className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-obsidian/50 backdrop-blur-sm p-0 sm:p-4">
      <div className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden animate-pop">
        <div className="flex justify-between items-center px-5 pt-4">
          <div className="flex gap-1.5">
            {STEPS.map((_, i) => (
              <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? "w-6 bg-sage" : "w-1.5 bg-obsidian/15"}`} />
            ))}
          </div>
          <button data-testid="tutorial-skip" onClick={finish} className="text-sm text-obsidian/50 hover:text-obsidian inline-flex items-center gap-1">
            Passer <X size={15} />
          </button>
        </div>

        <div className="px-7 py-8 text-center">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-sage/15 flex items-center justify-center">
            <Icon size={30} strokeWidth={1.6} className="text-sage" />
          </div>
          <h2 className="mt-5 text-2xl font-medium leading-tight">{s.title}</h2>
          <p className="mt-3 text-base text-obsidian/65 leading-relaxed">{s.body}</p>
        </div>

        <div className="px-7 pb-8 flex items-center gap-3">
          {step > 0 && (
            <button data-testid="tutorial-back" onClick={() => setStep((v) => v - 1)}
              className="rounded-full border border-border px-5 py-3 text-sm font-medium hover:bg-secondary transition-colors">
              Retour
            </button>
          )}
          {!isLast ? (
            <button data-testid="tutorial-next" onClick={() => setStep((v) => v + 1)}
              className="flex-1 rounded-full bg-obsidian text-white py-3.5 text-base font-medium inline-flex items-center justify-center gap-2 hover:-translate-y-0.5 transition-transform">
              Continuer <ArrowRight size={17} />
            </button>
          ) : (
            <button data-testid="tutorial-finish" onClick={async () => { await finish(); navigate("/profil"); }}
              className="flex-1 rounded-full bg-sage text-obsidian py-3.5 text-base font-medium inline-flex items-center justify-center gap-2 hover:-translate-y-0.5 transition-transform">
              C'est parti <ArrowRight size={17} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
